import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useIsMobile from "@app.automotus.io/components/hooks/useIsMobile";
import {
  PermitApplicationProvider,
  PermitApplicationStep,
  usePermitApplicationContext,
} from "../../PermitApplicationContext";
import { PermitApplicationStepper } from "../PermitApplicationStepper";
import { PermitApplicationNavigation } from "../PermitApplicationNavigation";
import { usePermitApplicationNavigation, usePermitSubmission } from "../../hooks";

interface PermitApplicationErrorProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const PermitApplicationError: React.FC<PermitApplicationErrorProps> = ({ error, resetErrorBoundary }) => (
  <Box sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="h6" color="error" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {error.message}
    </Typography>
    <Button variant="contained" onClick={resetErrorBoundary}>
      Try Again
    </Button>
  </Box>
);

const LayoutContent: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { currentStep, goToStep } = usePermitApplicationNavigation();
  const { submitApplication, isSubmitting } = usePermitSubmission();
  const { resetApplication } = usePermitApplicationContext();

  const handleBackToPermits = () => {
    resetApplication();
    navigate("/permits");
  };

  const handleStepClick = (step: PermitApplicationStep) => {
    goToStep(step);
  };

  // Default to first step if not found
  const activeStep = currentStep ?? PermitApplicationStep.SelectAuthority;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      {/* Back to Permits link */}
      <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBackToPermits} sx={{ mb: { xs: 2, md: 3 } }}>
        {isMobile ? "Back" : "Back to Permits"}
      </Button>

      {/* Title section */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Apply for a Permit
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete the steps below to submit your permit application
        </Typography>
      </Box>

      {/* Stepper */}
      <PermitApplicationStepper currentStep={activeStep} onStepClick={handleStepClick} />

      {/* Step content via Outlet */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <ErrorBoundary FallbackComponent={PermitApplicationError} onReset={() => navigate("/permits/apply/authority")}>
          <Outlet />
        </ErrorBoundary>
      </Paper>

      {/* Navigation buttons */}
      <PermitApplicationNavigation onSubmit={submitApplication} isSubmitting={isSubmitting} />
    </Container>
  );
};

/**
 * Route layout component for the multi-step permit application flow.
 * Wraps content in PermitApplicationProvider and renders:
 * - Back to Permits link
 * - Title and description
 * - Progress stepper (responsive)
 * - Step content via <Outlet />
 * - Navigation buttons (Back/Next/Submit)
 *
 * Submission is handled internally via usePermitSubmission hook.
 */
export const PermitApplicationLayout: React.FC = () => {
  return (
    <PermitApplicationProvider>
      <LayoutContent />
    </PermitApplicationProvider>
  );
};

export default PermitApplicationLayout;
